import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Labeled, NumberInput, TextInput } from '@tes/ui';
import {
  DOCUMENT_ORDER_RULE_LIMITS,
  SIGNING_ORDER_MAX,
  SIGNING_ORDER_MIN,
  UNORDERED_DEFAULT,
  matchDocumentOrderRule,
  type DocumentOrderRule,
} from '@tes/domain';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import { rowsChanged, rulesFromRows, type RuleRow } from '../lib/documentOrderRules';

/**
 * Settings → keyword rules that position envelope documents by a word in their
 * title or file name (e.g. "quote" → 1 puts uploaded quotations first).
 */
export function DocumentOrderRules({ rules }: { rules: DocumentOrderRule[] }) {
  // Re-seed the rows from the server only when the SAVED rules change, so a
  // background refetch never clobbers what someone is typing. That remount
  // also resets the form's state, so the "saved" notice lives up here.
  const [justSaved, setJustSaved] = useState(false);
  return <RulesForm key={JSON.stringify(rules)} saved={rules} justSaved={justSaved} onJustSaved={setJustSaved} />;
}

function RulesForm({
  saved,
  justSaved,
  onJustSaved,
}: {
  saved: DocumentOrderRule[];
  justSaved: boolean;
  onJustSaved: (value: boolean) => void;
}) {
  const qc = useQueryClient();
  const nextId = useRef(saved.length);
  const [rows, setRows] = useState<RuleRow[]>(() => saved.map((r, id) => ({ id, ...r })));
  const [sample, setSample] = useState('');
  const { rules, errors } = rulesFromRows(rows);
  const dirty = rowsChanged(saved, rows);

  const save = useMutation({
    mutationFn: () => api.patchSettings({ documentOrder: { keywordRules: rules } }),
    onSuccess: (updated) => {
      onJustSaved(true);
      qc.setQueryData(['settings'], updated);
    },
  });
  const touched = () => {
    save.reset();
    onJustSaved(false);
  };

  const edit = (id: number, patch: Partial<RuleRow>) => {
    touched();
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const add = () => {
    touched();
    setRows((current) => [...current, { id: nextId.current++, keyword: '', order: 1 }]);
  };
  const remove = (id: number) => {
    touched();
    setRows((current) => current.filter((r) => r.id !== id));
  };

  // Previews the rows as edited (valid ones only), so a rule can be tried before saving.
  const match = sample.trim() ? matchDocumentOrderRule(rules, [sample]) : null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-1 text-sm font-semibold text-slate-700">Document signing order</h2>
      <p className="mb-4 text-xs text-slate-500">
        Put documents in a set position by a word in their name. When an envelope is created, a document whose
        title or original file name <em>contains</em> a keyword (upper/lower case doesn&apos;t matter) takes that
        position — 1 is signed first. A position sent in the API request, or set on the template itself, takes
        priority. If several keywords match, the lowest position wins. Documents with no position sort as{' '}
        {UNORDERED_DEFAULT}; ties are alphabetical. Existing envelopes keep their order.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (dirty && !errors.length && !save.isPending) save.mutate();
        }}
      >
        <fieldset disabled={save.isPending} className="space-y-2">
          {rows.length > 0 && (
            <div className="grid grid-cols-[1fr_6rem_2.5rem] gap-2 text-xs font-medium text-slate-500">
              <span>Keyword</span>
              <span>Position</span>
            </div>
          )}
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-[1fr_6rem_2.5rem] items-center gap-2">
              <TextInput
                aria-label="Keyword"
                value={r.keyword}
                placeholder="e.g. quote"
                maxLength={DOCUMENT_ORDER_RULE_LIMITS.keywordMaxLength}
                spellCheck={false}
                onChange={(e) => edit(r.id, { keyword: e.target.value })}
              />
              <NumberInput
                aria-label="Position"
                min={SIGNING_ORDER_MIN}
                max={SIGNING_ORDER_MAX}
                step={1}
                value={r.order}
                onValueChange={(order) => edit(r.id, { order })}
              />
              <Button type="button" variant="ghost" size="sm" aria-label="Remove rule" onClick={() => remove(r.id)}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          {rows.length === 0 && <p className="text-sm text-slate-400">No rules — documents follow their template or API order.</p>}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={add}
              disabled={rows.length >= DOCUMENT_ORDER_RULE_LIMITS.maxRules}
            >
              <Plus size={14} className="mr-1" /> Add rule
            </Button>
            <Button type="submit" size="sm" disabled={!dirty || !!errors.length || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save order rules'}
            </Button>
            {dirty && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  touched();
                  setRows(saved.map((r, id) => ({ id, ...r })));
                }}
              >
                Discard changes
              </Button>
            )}
            {justSaved && !dirty && <span role="status" className="text-sm text-emerald-700">Order rules saved.</span>}
          </div>
          {!!errors.length && (
            <div role="alert" className="text-sm text-red-600">
              {errors.map((error) => <p key={error}>{error}</p>)}
            </div>
          )}
          {save.error && <p role="alert" className="text-sm text-red-600">{messageOf(save.error, 'Could not save order rules')}</p>}
        </fieldset>
      </form>

      <div className="mt-4 border-t border-slate-100 pt-4">
        <Labeled label="Try a document name" hint="Checks the rules above as you type, before saving.">
          <TextInput
            value={sample}
            placeholder="e.g. Quote QU-0042.pdf"
            spellCheck={false}
            onChange={(e) => setSample(e.target.value)}
          />
        </Labeled>
        {sample.trim() && (
          <p role="status" className="mt-2 text-sm text-slate-700">
            {match ? (
              <>
                Position <strong>{match.order}</strong> — contains &ldquo;{match.keyword}&rdquo;.
              </>
            ) : (
              <>
                No keyword matches — sorts as {UNORDERED_DEFAULT} unless its template or the API request sets a
                position.
              </>
            )}
          </p>
        )}
      </div>
    </section>
  );
}
